import "./Tooltip.scss";

import clsx from "clsx";
import { ReactNode, useEffect, useRef, useState } from "react";

import { FCC } from "@/types";

interface Props {
  className?: string;
  /**
   * Applied to the positioning container rather than the bubble — needed when
   * the trigger has to shrink with its parent (e.g. `min-w-0` inside a flex
   * row, so a clamped label can actually clip).
   */
  containerClassName?: string;
  content: string | ReactNode;
  dataTestId?: string;
  position?: "top" | "bottom";
  leftOnMobile?: boolean;
}

export const Tooltip: FCC<Props> = ({
  children,
  className,
  containerClassName,
  content,
  dataTestId,
  position = "top",
  leftOnMobile = false,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Close tooltip when clicking outside (mobile case)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={clsx("tooltip-container", containerClassName)}
      ref={containerRef}
      onMouseOver={() => {
        setIsVisible(true);
      }}
      onMouseLeave={() => {
        setIsVisible(false);
      }}
      // Focus mirrors hover so the tooltip is reachable without a pointer.
      // React routes these through focusin/focusout, so focus on any descendant
      // (the trigger button, a focusable clamped label) counts.
      onFocus={() => {
        setIsVisible(true);
      }}
      onBlur={() => {
        setIsVisible(false);
      }}
    >
      <div
        ref={tooltipRef}
        data-testid={dataTestId}
        className={clsx(
          "tooltip",
          className,
          position,
          leftOnMobile && "left-on-mobile",
          isVisible ? "visible" : "hidden",
        )}
      >
        {content}
      </div>
      <div onClick={() => setIsVisible((prev) => !prev)}>{children}</div>
    </div>
  );
};
